/**
 * QueryProvider — configures and provides the TanStack Query client.
 *
 * Default options:
 * - staleTime: 5 minutes (avoid redundant refetches for mostly-static health data).
 * - gcTime: 30 minutes (keep unused data in cache longer for offline-friendly UX).
 * - retry: 1 for non-auth failures; 401/403 are handled by apiClient.
 * - refetchOnWindowFocus: false (prevent jarring refetches when tabbing back).
 *
 * Must be placed *inside* AuthProvider so logout/auth-expiry can clear cache.
 */
import { useMemo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isApiError } from "@shared/lib/api/client";

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        retry: (failureCount, error) => {
          if (
            isApiError(error) &&
            (error.statusCode === 401 || error.statusCode === 403)
          ) {
            return false;
          }
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

// Singleton for the browser — avoids re-creating on re-renders.
let browserClient: QueryClient | undefined;

function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    // SSR: always create a new client to avoid cross-request leakage.
    return makeQueryClient();
  }
  if (!browserClient) {
    browserClient = makeQueryClient();
  }
  return browserClient;
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => getQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

/** Get the singleton query client (useful outside React tree, e.g. in loaders). */
export { getQueryClient };
